<template>
    <TransitionRoot as="template" :show="open">
        <Dialog class="relative z-100" @close="close">
            <TransitionChild
                as="template"
                enter="ease-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in duration-200"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-gray-500/75 transition-opacity" />
            </TransitionChild>

            <div class="fixed inset-0 z-100 w-screen overflow-y-auto">
                <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                            <form @submit.prevent="submit" class="flex flex-col divide-y divide-gray-200 bg-white">
                                <div class="px-4 py-4 sm:px-6">
                                    <div class="flex items-start justify-between">
                                        <div class="text-left">
                                            <DialogTitle class="text-base font-semibold text-gray-900">{{ $t('create_folder_modal.title') }}</DialogTitle>
                                            <p class="mt-1 text-sm text-gray-500">{{ $t('folders.tip_text') }}</p>
                                        </div>
                                        <button type="button" class="relative rounded-md text-gray-400 hover:text-gray-500 focus-visible:ring-2 focus-visible:ring-royal-blue-500 focus:outline-hidden" @click="close">
                                            <span class="absolute -inset-2.5" />
                                            <span class="sr-only">{{ $t('create_folder_modal.close') }}</span>
                                            <XMarkIcon class="size-6" aria-hidden="true" />
                                        </button>
                                    </div>
                                </div>

                                <div class="px-4 py-5 sm:px-6 space-y-6">
                                    <div>
                                        <label for="title" class="block text-sm/6 font-medium text-gray-900">{{ $t('create_folder_modal.name') }}</label>
                                        <div class="mt-2 grid grid-cols-1">
                                            <input
                                                id="title"
                                                name="text"
                                                v-model="form.title"
                                                type="text"
                                                autocomplete="title"
                                                :class="{
                                                    'block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-royal-blue-600 sm:text-sm/6': true,
                                                    'col-start-1 row-start-1 block w-full rounded-md bg-white py-1.5 pr-10 pl-3 text-base text-red-900 outline-1 -outline-offset-1 outline-red-300 placeholder:text-red-300 focus:outline-2 focus:-outline-offset-2 focus:outline-red-600 sm:pr-9 sm:text-sm/6': hasError('title')
                                                }" />
                                            <ExclamationCircleIcon v-if="hasError('title')" class="pointer-events-none col-start-1 row-start-1 mr-3 size-5 self-center justify-self-end text-red-500 sm:size-4" aria-hidden="true" />
                                        </div>
                                        <p class="mt-2 text-sm text-red-600 text-left" id="title-error" v-if="hasError('title')">{{ getError('title') }}</p>
                                    </div>

                                    <div>
                                        <SwitchGroup as="div" class="flex items-center justify-between">
                                            <span class="flex grow flex-col mr-3 text-left">
                                                <SwitchLabel as="span" class="text-sm/6 font-medium text-gray-900" passive>
                                                    {{ $t('create_folder_modal.hide_label') }}
                                                </SwitchLabel>
                                                <SwitchDescription as="span" class="text-sm text-gray-500">
                                                    {{ $t('create_folder_modal.hide_description') }}
                                                </SwitchDescription>
                                            </span>

                                            <Switch v-model="form.is_hidden" :class="[form.is_hidden ? 'bg-royal-blue-600' : 'bg-gray-200', 'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:ring-2 focus:ring-royal-blue-600 focus:ring-offset-2 focus:outline-hidden']">
                                                <span aria-hidden="true" :class="[form.is_hidden ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none inline-block size-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out']" />
                                            </Switch>
                                        </SwitchGroup>
                                    </div>

                                    <div>
                                        <SwitchGroup as="div" class="flex items-center justify-between">
                                            <span class="flex grow flex-col mr-3 text-left">
                                                <SwitchLabel as="span" class="text-sm/6 font-medium text-gray-900" passive>
                                                    {{ $t('create_folder_modal.hide_photos_label') }}
                                                </SwitchLabel>
                                                <SwitchDescription as="span" class="text-sm text-gray-500">
                                                    {{ $t('create_folder_modal.hide_photos_description') }}
                                                </SwitchDescription>
                                            </span>

                                            <Switch v-model="form.hide_photos" :class="[form.hide_photos ? 'bg-royal-blue-600' : 'bg-gray-200', 'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:ring-2 focus:ring-royal-blue-600 focus:ring-offset-2 focus:outline-hidden']">
                                                <span aria-hidden="true" :class="[form.hide_photos ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none inline-block size-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out']" />
                                            </Switch>
                                        </SwitchGroup>
                                    </div>
                                </div>

                                <div class="flex shrink-0 justify-end px-4 py-4 bg-gray-50">
                                    <button type="button" class="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 shadow-xs ring-gray-300 ring-inset hover:bg-gray-50 cursor-pointer" @click="close">{{ $t('create_folder_modal.cancel') }}</button>
                                    <button type="submit" :disabled="isCreating" class="ml-4 inline-flex justify-center rounded-md bg-royal-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-royal-blue-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-royal-blue-600 cursor-pointer">{{ $t('create_folder_modal.create') }}</button>
                                </div>
                            </form>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script lang="ts">
import {defineComponent} from 'vue';

import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { Switch, SwitchDescription, SwitchGroup, SwitchLabel } from '@headlessui/vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import {mapActions, mapGetters} from "vuex";
import {validationMethods} from "@/_helpers/validation";
import {ResponseError} from "@/types";
import {ExclamationCircleIcon} from "@heroicons/vue/16/solid";
import {createFolder} from "@/data/requests/gallery";
import {Folder} from "@/types/GalleryType";

export default defineComponent({
    name: 'CreateFolderComponent',
    components: {
        ExclamationCircleIcon,
        Dialog,
        DialogPanel,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        XMarkIcon,
        Switch, SwitchDescription, SwitchGroup, SwitchLabel
    },

    props: {
        open: {
            type: Boolean,
            required: true,
        },

        gallerySlug: {
            type: String,
            required: true,
        }
    },

    data: function () {
        return {
            isCreating: false,

            form: {
                title: "",
                is_hidden: false,
                hide_photos: false,
            } as { title: string, is_hidden: boolean, hide_photos: boolean },

            errors: {} as { [key: string]: string[] },
        }
    },

    watch: {
        'form.title': function() {
            delete this.errors['title'];
        },
        'form.is_hidden': function() {
            delete this.errors['is_hidden'];
        },
    },

    computed: {
        ...mapGetters({
            galleries: "galleries/galleries",
            hasGalleries: "galleries/hasGalleries"
        })
    },

    mounted() {
        //
    },

    methods: {
        close() {
            this.$emit('close');
        },

        ...mapActions({
            loadGalleries: "galleries/loadGalleries",
            loadFolders: "galleries/loadFolders",

            errorAlert: "alerts/error"
        }),

        ...validationMethods(),

        selectFolder(folder: Folder | null) {
            if(folder) {
                this.close();
                this.$router.push(`/galleries/${this.gallerySlug}/photos#${folder.slug}`);
            }
        },

        submit() {
            if(this.isCreating) {
                return;
            }

            this.isCreating = true;

            createFolder(this.gallerySlug, this.form, (folder) => {
                this.loadFolders({gallery: this.gallerySlug});

                this.isCreating = false;
                this.form = {
                    title: "",
                    is_hidden: false,
                    hide_photos: false,
                };

                this.selectFolder(folder);
            }, (type, data) => {
                this.isCreating = false;

                if(type == ResponseError.Validation) {
                    this.errors = data.data;

                    this.errorAlert({
                        message: this.$t('create_folder_modal.validation_error'),
                    });
                } else {
                    this.errorAlert({
                        message: data.message,
                    });
                }
            });
        }
    }
});
</script>